import React, { useEffect } from "react";
import { connect } from "react-redux";

import { IonButton, useIonAlert } from "@ionic/react";
import { useToasts } from "components/Toast/toast";

import { loadQuiz, submitResults } from "services/quiz";
import { logoutUser } from "services/users";

export interface Props {
  user: any;
  teams: any;
  quizData: Quiz;
  submitResults: Function;
  logoutUser: Function;
  trigger: Number | Boolean;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      ["google-cast-launcher"]: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

const FinaliseButton: React.FC<Props> = ({
  user,
  teams,
  quizData,
  submitResults,
  logoutUser,
  trigger,
}) => {
  const [presentAlert] = useIonAlert();
  const { addToast } = useToasts();

  const onFinalizeFail = () => {
    addToast({
      message: "Finalise Failed",
      duration: 3000,
      color: "danger",
    });
    presentAlert({
      header: "Alert",
      subHeader: "Finalise Failed",
      message: "The quiz results were <b>NOT finalised</b> successfully.",
      buttons: [
        {
          text: "OK",
          role: "cancel",
        },
        {
          text: "Retry",
          role: "confirm",
          handler: () => {
            setTimeout(() => {
              onFinaliseResults();
            }, 500);
          },
        },
      ],
    });
  };

  const onFinalizeSuccess = () => {
    addToast({
      message: "Finalise Successful",
      duration: 3000,
      color: "success",
    });
    presentAlert({
      header: "Success",
      subHeader: "Results Finalised",
      message: "The quiz results were successfully finalised.",
      buttons: [
        {
          text: "OK",
          role: "confirm",
        },
        {
          text: "Logout",
          role: "cancel",
          handler: () => {
            logoutUser();
          },
        },
      ],
    });
  };

  // console.log(teams);

  const onFinaliseResults = () => {
    if (teams?.length > 0) {
      if (quizData.resultsSubmitted) {
        presentAlert({
          header: "Alert",
          subHeader: "Refinalise Results",
          message:
            "You are about to refinalise the results. This will overwrite the previous results. Are you sure you want to continue?",
          buttons: [
            {
              text: "OK",
              role: "confirm",
              handler: (alertData) => {
                if (alertData?.reason !== "") {
                  submitResults(teams, quizData.quizConfig, user)
                    .then((res: any) => {
                      if (res.status === 200) {
                        onFinalizeSuccess();
                      } else {
                        onFinalizeFail();
                      }
                    })
                    .catch(() => {
                      onFinalizeFail();
                    });
                } else {
                  addToast({
                    message: "Please provide a reason for refinalising",
                    duration: 3000,
                    color: "danger",
                  });
                }
              },
            },
            {
              text: "Cancel",
              role: "cancel",
            },
          ],
          inputs: [
            {
              name: "reason",
              type: "textarea",
              placeholder: "Tell us why you want to refinalise the results",
            },
          ],
        });
      } else {
        presentAlert({
          header: "Alert",
          subHeader: "Finalise Results",
          message:
            "Are you sure you want to finalise results? This action cannot be done again.",
          buttons: [
            {
              text: "Cancel",
              role: "cancel",
            },
            {
              text: "OK",
              role: "confirm",
              handler: () => {
                submitResults(teams, quizData.quizConfig, user).then(
                  (res: any) => {
                    if (res.status === 200) {
                      onFinalizeSuccess();
                    } else {
                      onFinalizeFail();
                    }
                  }
                );
              },
            },
          ],
        });
      }
    } else {
      presentAlert({
        header: "Alert",
        subHeader: "No Teams Found",
        message: "Please add teams and their results before finalising.",
        buttons: [
          {
            text: "Cancel",
            role: "cancel",
          },
          {
            text: "OK",
            role: "cancel",
          },
        ],
      });
    }
  };

  useEffect(() => {
    if (trigger) {
      onFinaliseResults();
    }
    //eslint-disable-next-line
  }, [trigger]);

  return (
    <IonButton size="default" color="danger" onClick={onFinaliseResults}>
      Finalise Results
    </IonButton>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user,
  quizData: state.quiz,
  teams: state.teams.teams,
});

export default connect(mapStateToProps, {
  logoutUser,
  loadQuiz,
  submitResults,
})(FinaliseButton);
